import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface CookieContextType {
  cookieConsent: CookieConsent | null;
  updateCookieConsent: (consent: Partial<CookieConsent>) => void;
  showConsentBanner: boolean;
  acceptAllCookies: () => void;
  rejectAllCookies: () => void;
}

const CookieContext = createContext<CookieContextType>({
  cookieConsent: null,
  updateCookieConsent: () => {},
  showConsentBanner: true,
  acceptAllCookies: () => {},
  rejectAllCookies: () => {},
});

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_EXPIRY = 365; // days

export function CookieProvider({ children }: { children: React.ReactNode }) {
  const [cookieConsent, setCookieConsent] = useState<CookieConsent | null>(null);
  const [showConsentBanner, setShowConsentBanner] = useState(true);

  useEffect(() => {
    const savedConsent = Cookies.get(COOKIE_CONSENT_KEY);
    if (savedConsent) {
      try {
        const parsedConsent = JSON.parse(savedConsent);
        setCookieConsent(parsedConsent);
        setShowConsentBanner(false);
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
      }
    }
  }, []);

  const updateCookieConsent = (consent: Partial<CookieConsent>) => {
    const newConsent = {
      ...cookieConsent,
      ...consent,
      necessary: true, // Always required
    };
    setCookieConsent(newConsent as CookieConsent);
    setShowConsentBanner(false);
    Cookies.set(COOKIE_CONSENT_KEY, JSON.stringify(newConsent), { expires: COOKIE_EXPIRY });
  };

  const acceptAllCookies = () => {
    const fullConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    updateCookieConsent(fullConsent);
  };

  const rejectAllCookies = () => {
    const minimalConsent: CookieConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    updateCookieConsent(minimalConsent);
  };

  return (
    <CookieContext.Provider
      value={{
        cookieConsent,
        updateCookieConsent,
        showConsentBanner,
        acceptAllCookies,
        rejectAllCookies,
      }}
    >
      {children}
    </CookieContext.Provider>
  );
}

export const useCookies = () => useContext(CookieContext);